import { useMemo } from "react";
import GameState from "../../../src/enum/gamestate";
import { ApiGame } from "../../../src/types/apigame";
import { getSeatObjectsAndInvitedPlayers } from "../../../src/util/gameslist";
import UserPlayerAvatar, { UserPlayerAvatarSize } from "../../user/playeravatar";
import styles from './gameslistgame.module.css';
import { GamePrivacy } from "../../../src/enum/game";

type Props = {
    game: ApiGame;
    size?: UserPlayerAvatarSize;
}

export default function GamesListAvatars(
    {
        game,
        size,
    }: Props,
): JSX.Element {
    const hasGameStarted = game.state !== GameState.Created;

    const avatars = useMemo(
        () => {
            const visibleSeats = getSeatObjectsAndInvitedPlayers(game);
            const avatarItems = visibleSeats
                .sort((a, b) => {
                    if (game.state === GameState.Created) {
                        const creatorOrder = Number(b.userId === game.creatorId)
                            - Number(a.userId === game.creatorId);

                        if (creatorOrder !== 0) {
                            return creatorOrder;
                        }
                    }

                    const seatNumberOrder = (a.seatNumber ?? Number.MAX_SAFE_INTEGER)
                        - (b.seatNumber ?? Number.MAX_SAFE_INTEGER);

                    return seatNumberOrder || a.id - b.id;
                })
                .map(
                    (seat) => <UserPlayerAvatar
                        game={game}
                        hasGameStarted={hasGameStarted}
                        key={`seat-${seat.id}`}
                        seatObject={seat}
                        showProfileOnClick={false}
                        size={size}
                    />
                );

            if (hasGameStarted || game.privacy !== GamePrivacy.AnyoneCanJoin) {
                return avatarItems;
            }

            for (let i = visibleSeats.length; i < game.maxSeats; ++i) {
                avatarItems.push(<UserPlayerAvatar
                    game={game}
                    hasGameStarted={hasGameStarted}
                    key={`empty-${i}`}
                    showProfileOnClick={false}
                    size={size}
                />);
            }

            return avatarItems;
        },
        [game, hasGameStarted, size],
    );

    const renderAvatarRow = (capacity: number, className: string): JSX.Element => {
        const hasOverflow = avatars.length > capacity;
        const visibleCount = hasOverflow ? capacity - 1 : avatars.length;
        const overflowCount = avatars.length - visibleCount;

        return <div className={`${styles.avatars} ${className}`}>
            {avatars.slice(0, visibleCount)}
            {
                hasOverflow && <span
                    className={styles.avatarOverflow}
                    title={`${overflowCount} more players`}
                >
                    +{overflowCount} more
                </span>
            }
        </div>;
    };

    return <>
        {renderAvatarRow(15, styles.avatarsWide)}
        {renderAvatarRow(10, styles.avatarsNarrow)}
    </>;
}
